import { useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuBookOpen } from 'react-icons/lu';
import DashboardLayout from './layout';
import { EmptyState, Mascot, Scene, SectionHeader } from '../../design-system/components';
import {
  useDomainStore,
  type MobileFilterMode,
  type CourseContext,
} from '../../stores/domain.store';
import {
  DomainAccordion,
  PathAccordion,
  LessonCard,
  CourseSidebar,
  MobileFilterPills,
} from '../../components/course';
import { nextLessonId, scrollTargetLesson } from '../../components/course/lessonMarker';
import { useIsDesktop, useIsTabletUp } from '../../hooks/useMediaQuery';
import type { DomainWithPaths, LessonWithProgress } from '../../types';
import { fireAndForget } from '../../utils/fireAndForget';

// Scroll to the current lesson after lessons load
function useScrollToCurrentLesson(
  pathLessons: Record<number, unknown[]>,
  loadingPaths: Record<number, boolean>,
  expandToPath: (pathId: number) => void,
  findTargetLessonPath: () => { pathId: number; lessonId: number } | null,
) {
  const hasScrolled = useRef(false);
  const hasExpandedTarget = useRef(false);

  useEffect(() => {
    // Only scroll once after initial load
    if (hasScrolled.current) return;

    // Check if any path has finished loading lessons
    const hasLoadedLessons = Object.keys(pathLessons).length > 0;
    const isStillLoading = Object.values(loadingPaths).some((loading) => loading);

    if (hasLoadedLessons && !isStillLoading) {
      // Find target lesson and expand its path if needed
      if (!hasExpandedTarget.current) {
        const target = findTargetLessonPath();
        if (target) {
          expandToPath(target.pathId);
          hasExpandedTarget.current = true;
        }
      }

      // Small delay to ensure DOM is rendered after framer-motion animations
      const timer = setTimeout(() => {
        // Priority order: in-progress > next > last (lessonMarker.ts)
        const targetLesson = scrollTargetLesson(document);

        if (targetLesson) {
          targetLesson.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolled.current = true;
        }
      }, 500); // Wait for accordion expansion + stagger animation

      return () => clearTimeout(timer);
    }
  }, [pathLessons, loadingPaths, expandToPath, findTargetLessonPath]);
}

// Filter domains based on mobile filter mode
// Note: 'path' and 'lesson' modes are rendered directly, not through this filter
function filterDomainsForMobile(
  domains: DomainWithPaths[],
  filter: MobileFilterMode,
  context: CourseContext | null,
): DomainWithPaths[] {
  // Only 'domain' mode uses filtering; 'path' and 'lesson' render directly
  if (!context || filter !== 'domain') return domains;
  return domains.filter((d) => d.id === context.domainId);
}

// Filter lessons for lesson-level filtering
function filterLessonsForMode(
  lessons: LessonWithProgress[],
  filter: MobileFilterMode,
  context: CourseContext | null,
): LessonWithProgress[] {
  if (filter !== 'lesson' || !context?.lessonId) return lessons;
  return lessons.filter((l) => l.id === context.lessonId);
}

// Loading spinner component
const LoadingSpinner = () => (
  <div className="ee-course-loading-block" role="status">
    <div className="loading loading-dots loading-lg ee-course-loading"></div>
    <p>Loading your course...</p>
  </div>
);

function Course() {
  const isDesktop = useIsDesktop();
  // The heading band's art: the Scene from 768, the glasses giraffe below.
  // Each is mounted only where it shows, so a phone never loads the Scene.
  const hasBand = useIsTabletUp();
  const location = useLocation();

  // Core course state
  const domains = useDomainStore((state) => state.domains);
  const isLoading = useDomainStore((state) => state.isLoading);
  const fetchDomains = useDomainStore((state) => state.fetchDomains);
  const expandedDomains = useDomainStore((state) => state.expandedDomains);
  const expandedPaths = useDomainStore((state) => state.expandedPaths);
  const pathLessons = useDomainStore((state) => state.pathLessons);
  const loadingPaths = useDomainStore((state) => state.loadingPaths);
  const errorPaths = useDomainStore((state) => state.errorPaths);
  const toggleDomain = useDomainStore((state) => state.toggleDomain);
  const togglePath = useDomainStore((state) => state.togglePath);
  const retryFetchLessonsForPath = useDomainStore((state) => state.retryFetchLessonsForPath);
  const expandFirstDomainAndPath = useDomainStore((state) => state.expandFirstDomainAndPath);
  const expandToPath = useDomainStore((state) => state.expandToPath);
  const findTargetLessonPath = useDomainStore((state) => state.findTargetLessonPath);

  // Navigation state
  const sidebarCollapsed = useDomainStore((state) => state.sidebarCollapsed);
  const mobileFilter = useDomainStore((state) => state.mobileFilter);
  const currentContext = useDomainStore((state) => state.currentContext);
  const toggleSidebar = useDomainStore((state) => state.toggleSidebar);
  const setMobileFilter = useDomainStore((state) => state.setMobileFilter);
  const initializeContext = useDomainStore((state) => state.initializeContext);
  const navigateToSection = useDomainStore((state) => state.navigateToSection);

  // Filtered domains based on current context (used for both desktop and mobile)
  const filteredDomains = useMemo(() => {
    // For desktop, show only the current domain
    if (isDesktop) {
      if (!currentContext?.domainId) return domains;
      return domains.filter((d) => d.id === currentContext.domainId);
    }
    // For mobile, use the filter mode (only 'domain' mode filters here)
    return filterDomainsForMobile(domains, mobileFilter, currentContext);
  }, [domains, isDesktop, mobileFilter, currentContext]);

  // Filtered pathLessons for lesson-level filtering
  const filteredPathLessons = useMemo(() => {
    if (isDesktop || mobileFilter !== 'lesson') return pathLessons;
    const filtered: Record<number, LessonWithProgress[]> = {};
    for (const [pathId, lessons] of Object.entries(pathLessons)) {
      filtered[Number(pathId)] = filterLessonsForMode(lessons, mobileFilter, currentContext);
    }
    return filtered;
  }, [pathLessons, isDesktop, mobileFilter, currentContext]);

  // Fetch domains on mount and on every navigation to this page.
  // React Router may keep Course mounted during fast transitions (concurrent mode),
  // so we use location.key to detect re-navigation even without unmount/remount.
  useEffect(() => {
    if (domains.length > 0) {
      void fetchDomains(true); // silent refresh — no loading spinner for cached data
    } else {
      void fetchDomains(); // initial load — show loading spinner
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Refetch domains when page becomes visible (e.g., returning from lesson/quiz)
  // This ensures lock status is updated after quiz completion without manual refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && domains.length > 0) {
        // Page became visible and we have domains cached - silent refresh (no spinner)
        void fetchDomains(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchDomains, domains.length]);

  // Auto-expand first domain and path after domains load
  useEffect(() => {
    if (domains.length > 0 && Object.keys(expandedDomains).length === 0) {
      expandFirstDomainAndPath();
    }
  }, [domains, expandedDomains, expandFirstDomainAndPath]);

  // Initialize context when lessons load (for mobile filters)
  useEffect(() => {
    if (Object.keys(pathLessons).length > 0 && !currentContext) {
      initializeContext();
    }
  }, [pathLessons, currentContext, initializeContext]);

  // Scroll to current lesson after lessons load
  useScrollToCurrentLesson(pathLessons, loadingPaths, expandToPath, findTargetLessonPath);

  // Handle sidebar domain toggle - also updates the main content view
  const handleSidebarDomainToggle = (domainId: number) => {
    // navigateToSection handles toggling the domain and updating context
    navigateToSection(domainId);
  };

  // Handle sidebar path click
  const handleSidebarPathClick = (domainId: number, pathId: number) => {
    navigateToSection(domainId, pathId);
  };

  // The phone's filter pills; the glasses giraffe tucks behind them, so it
  // shows only when they do (loading and the empty state have neither), and
  // only below 768 where there is no band.
  const showPills = !isDesktop && !isLoading && domains.length > 0;
  const showPeek = showPills && !hasBand;

  // Redesign PR4 (2026-10-20): the page opts into the design-system scope
  // (.ee-page: ink and fonts) on its own root, and the layout paints the
  // canvas, the way the 1-on-1 Empathy page does. Nothing global changes.
  // A §7a calm hub: the giraffe coat in dark (a no-op in light, D10).
  return (
    <DashboardLayout background="var(--ee-canvas)" coat>
      <div data-testid="course-page" className="ee-page">
        {/* Desktop Sidebar */}
        {isDesktop ? (
          <CourseSidebar
            domains={domains}
            isCollapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            expandedDomains={expandedDomains}
            currentPathId={currentContext?.pathId ?? null}
            onDomainToggle={handleSidebarDomainToggle}
            onPathClick={handleSidebarPathClick}
          />
        ) : null}

        {/* Main Content */}
        {/* Padding and the sidebar offset: .ee-course__main in course.css. */}
        <motion.div
          className={`ee-course__main ${isDesktop && !sidebarCollapsed ? 'ee-course__main--beside-side' : ''}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Heading band: the course scene with the walking giraffe from 768
            (behind the heading band only, never behind body text); below
            768 no band, the glasses giraffe beside the heading with its cut
            edge tucked behind the filter pills. All decorative (course.css). */}
          <div className={`ee-course-hero ${showPeek ? 'ee-course-hero--peek' : ''}`}>
            {hasBand ? (
              <Scene
                section="course"
                variant="hero"
                mascot="walk"
                fade={['left', 'bottom']}
                className="ee-course-hero__scene"
              />
            ) : null}
            {showPeek ? (
              <span aria-hidden="true" className="ee-course-peek">
                <Mascot name="glasses" loading="eager" />
              </span>
            ) : null}
            <SectionHeader
              title="Course 📖"
              tagline="Your learning journey"
              className="ee-course-hero__heading"
            />
          </div>

          {/* Mobile Filter Pills */}
          {showPills ? (
            <MobileFilterPills
              activeFilter={mobileFilter}
              onFilterChange={setMobileFilter}
              context={currentContext}
            />
          ) : null}

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <motion.div
              className="ee-course-body"
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
            >
              {/* Render based on filter mode */}
              {!isDesktop && mobileFilter === 'lesson' && currentContext?.pathId
                ? // Lesson mode: Show only the lesson card
                  (() => {
                    const lessons = filteredPathLessons[currentContext.pathId] || [];
                    const currentLesson = lessons.find((l) => l.id === currentContext.lessonId);
                    return currentLesson ? (
                      <div className="ee-lesson-rail ee-lesson-rail--single">
                        <LessonCard
                          lesson={currentLesson}
                          isCurrent
                          isNext={
                            currentLesson.id ===
                            nextLessonId(pathLessons[currentContext.pathId] || [])
                          }
                        />
                      </div>
                    ) : null;
                  })()
                : !isDesktop && mobileFilter === 'path' && currentContext?.pathId
                  ? // Path/Chapter mode: Show only the path accordion
                    (() => {
                      const domain = domains.find((d) => d.id === currentContext.domainId);
                      const path = domain?.paths?.find((p) => p.id === currentContext.pathId);
                      return path ? (
                        <PathAccordion
                          path={path}
                          isExpanded={true}
                          onToggle={() => togglePath(path.id)}
                          lessons={pathLessons[path.id] || []}
                          isLoadingLessons={loadingPaths[path.id] || false}
                          hasError={errorPaths[path.id] || false}
                          onRetry={fireAndForget(() => retryFetchLessonsForPath(path.id))}
                        />
                      ) : null;
                    })()
                  : // Full or Domain mode: Show domain accordions
                    filteredDomains.map((domain) => (
                      <DomainAccordion
                        key={domain.id}
                        domain={domain}
                        isExpanded={expandedDomains[domain.id] || mobileFilter !== 'full'}
                        onToggle={() => toggleDomain(domain.id)}
                        expandedPaths={expandedPaths}
                        onTogglePath={togglePath}
                        pathLessons={filteredPathLessons}
                        loadingPaths={loadingPaths}
                        errorPaths={errorPaths}
                        onRetryPath={fireAndForget(retryFetchLessonsForPath)}
                        forcePathsExpanded={
                          !isDesktop && mobileFilter !== 'full' && mobileFilter !== 'domain'
                        }
                      />
                    ))}

              {/* Empty State */}
              {domains.length === 0 && !isLoading && (
                <EmptyState
                  icon={LuBookOpen}
                  title="No Courses Available"
                  body="Check back later for new learning content."
                />
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
}

export default Course;
